import React from 'react';
import useGameStore from '../engine/store';

const DIFFICULTIES = [
  {
    value: 'easy', emoji: '🟢', label: 'Easy',
    capital: '$10M', desc: 'Generous starting capital. Fewer crises. Forgiving markets.',
  },
  {
    value: 'medium', emoji: '🟡', label: 'Medium',
    capital: '$5M', desc: 'Balanced challenge. Realistic market conditions.',
  },
  {
    value: 'hard', emoji: '🔴', label: 'Hard',
    capital: '$2M', desc: 'Tight capital. Frequent crises. One mistake can end you.',
  },
];

export default function SetupScreen() {
  const { difficulty, setDifficulty, startGame, loadGame, hasSave } = useGameStore();
  const canLoad = hasSave();

  return (
    <div className="min-h-screen bg-dark-900 bg-grid flex items-center justify-center p-4">
      <div className="max-w-3xl w-full">
        {/* Logo */}
        <div className="text-center mb-14 animate-slide-up">
          <div className="text-7xl mb-5">🏦</div>
          <h1 className="text-5xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-neon-green via-neon-blue to-neon-purple leading-tight">
            Bank Empire
          </h1>
          <p className="text-gray-400 mt-4 text-lg max-w-md mx-auto">
            Issue loans. Manage risk. Build a banking dynasty — or crash trying.
          </p>
        </div>

        {/* Difficulty selection */}
        <div className="mb-10 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="text-center text-sm uppercase tracking-widest text-gray-500 mb-6">Select Difficulty</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {DIFFICULTIES.map(d => (
              <button
                key={d.value}
                onClick={() => setDifficulty(d.value)}
                className={`p-6 rounded-xl border-2 transition-all duration-300 text-left hover:scale-[1.03] active:scale-95 ${
                  difficulty === d.value
                    ? 'border-neon-blue bg-neon-blue/10 shadow-lg shadow-neon-blue/20'
                    : 'border-white/10 bg-dark-700/60 hover:border-white/20'
                }`}
              >
                <div className="text-3xl mb-2">{d.emoji}</div>
                <div className="text-xl font-bold text-white">{d.label}</div>
                <div className="text-sm font-mono text-neon-green mt-1">Starting: {d.capital}</div>
                <div className="text-xs text-gray-500 mt-2">{d.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* How to play */}
        <div className="glass-panel p-6 mb-8 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-4 text-center">How It Works</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl mb-2">📋</div>
              <div className="text-xs text-gray-400">Review loan requests each month</div>
            </div>
            <div>
              <div className="text-2xl mb-2">✅</div>
              <div className="text-xs text-gray-400">Approve or reject. Set your interest rate.</div>
            </div>
            <div>
              <div className="text-2xl mb-2">💵</div>
              <div className="text-xs text-gray-400">Collect repayments. Watch for defaults.</div>
            </div>
            <div>
              <div className="text-2xl mb-2">⚡</div>
              <div className="text-xs text-gray-400">React to economic events and crises.</div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <button onClick={startGame} className="btn-primary text-lg px-12 py-4 w-full sm:w-auto">
            🏦 Open Your Bank
          </button>
          {canLoad && (
            <button
              onClick={() => loadGame()}
              className="btn-secondary text-lg px-8 py-4 w-full sm:w-auto"
            >
              📂 Load Game
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
